import React, { useState, useEffect, useMemo,useCallback  } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Layout, theme,
  Progress, Space, Spin, FloatButton, DatePicker, Button,
  Table, Tag, Avatar,Segmented ,Select 
} from 'antd';
import {
  ProjectOutlined, CheckCircleOutlined, WarningOutlined,
  FolderOpenOutlined, PauseCircleOutlined, CalendarOutlined,
  SyncOutlined, UpOutlined, DashboardOutlined, TeamOutlined,
  UserOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import api from '../api/Api';
import dayjs from 'dayjs';
import "./dashboard.css"; 

const { Title, Text } = Typography;
const { Content } = Layout;
const { useToken } = theme;
const { RangePicker } = DatePicker;

const DashboardAdministracao = () => {
  const { token } = useToken();

  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs()
  ]);
  const [loading, setLoading] = useState(false);

  const [projetosDirecoes, setProjetosDirecoes] = useState([]);
  const [tarefasMensaisDirecoes, setTarefasMensaisDirecoes] = useState([]);
  const [assiduidadeDirecoes, setAssiduidadeDirecoes] = useState([]);
  const [direcaoSelecionada, setDirecaoSelecionada] = useState('todas');
 

const fetchData = useCallback(async () => {
    
    setLoading(true);
    try {
      const inicio = dateRange?.[0] || dayjs().startOf('month');
      const fim = dateRange?.[1] || dayjs();
  
      const dataInicio = inicio.format('YYYY-MM-DD');
      const dataFim = fim.format('YYYY-MM-DD');

          const payload = {
      dataInicio: dataInicio,
     dataFim: dataFim,
     
    };

      const [projetosRes, tarefasMesRes, assiduidadeRes] = await Promise.all([
        api.ListIndicadoresProjetosAdministracao(payload),
        api.ListTarefasFechadasMesTodasDirecoes(payload),
        api.ListAssiduidadeTodasDirecoes(payload)
      ]);

      setProjetosDirecoes(projetosRes?.data || []);
      setTarefasMensaisDirecoes(tarefasMesRes?.data || []);
      setAssiduidadeDirecoes(assiduidadeRes?.data || []);
    } catch (error) {
      console.error("Erro ao buscar dados da administração:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]); // ← depende de dateRange

  // 2. Título só no mount
  useEffect(() => {
    document.title = "Dashboard da Administração - TECH NEXT CRM";
  }, []);

  // 3. Dispara no mount e sempre que dateRange mudar
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  

  // Agregações para projetos
  const totalProjetosGeral = useMemo(() =>
    projetosDirecoes.reduce((sum, dir) => sum + dir.total_projetos, 0),
    [projetosDirecoes]
  );
  const fechadosGeral = useMemo(() =>
    projetosDirecoes.reduce((sum, dir) => sum + dir.fechados, 0),
    [projetosDirecoes]
  );
  const foraPrazoGeral = useMemo(() =>
    projetosDirecoes.reduce((sum, dir) => sum + dir.fora_prazo, 0),
    [projetosDirecoes]
  );
  const completionPercent = totalProjetosGeral > 0
    ? Math.round((fechadosGeral / totalProjetosGeral) * 100)
    : 0;
  const overduePercent = totalProjetosGeral > 0
    ? Math.round((foraPrazoGeral / totalProjetosGeral) * 100)
    : 0;

  // Agregação mensal de tarefas (soma por mês sobre todas as direções)
  const tarefasMensaisAgregado = useMemo(() => {
    if (!tarefasMensaisDirecoes.length) return [];
    const meses = tarefasMensaisDirecoes[0].meses.map(m => m.mes);
    return meses.map(mes => {
      const total = tarefasMensaisDirecoes.reduce((acc, dir) => {
        const mesData = dir.meses.find(m => m.mes === mes);
        return acc + (mesData ? mesData.total : 0);
      }, 0);
      return { mes, total };
    });
  }, [tarefasMensaisDirecoes]);

  // Cards principais (estilo gradiente)
  const ProjetosCards = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={8}>
        <Card hoverable style={{
          background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
          borderRadius: '12px', border: 'none',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%'
        }} bodyStyle={{ padding: '20px' }}>
          <Statistic
            title={<Space style={{ color: 'white', opacity: 0.9 }}><ProjectOutlined /><Text style={{ color: 'white' }}>Total de Projetos</Text></Space>}
            value={totalProjetosGeral}
            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
            prefix={<ProjectOutlined />}
          />
          <div style={{ marginTop: 12 }}>
            <Progress percent={completionPercent} strokeColor="white"
              trailColor="rgba(255,255,255,0.2)" showInfo={false} size="small" />
            <Text style={{ color: 'white', fontSize: 12, display: 'block', marginTop: 4 }}>
              {completionPercent}% concluídos
            </Text>
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Card hoverable style={{
          background: `linear-gradient(135deg, ${token.colorSuccess} 0%, ${token.colorSuccessActive} 100%)`,
          borderRadius: '12px', border: 'none',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%'
        }} bodyStyle={{ padding: '20px' }}>
          <Statistic
            title={<Space style={{ color: 'white', opacity: 0.9 }}><CheckCircleOutlined /><Text style={{ color: 'white' }}>Concluídos</Text></Space>}
            value={fechadosGeral}
            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
            prefix={<CheckCircleOutlined />}
          />
          <div style={{ marginTop: 12 }}>
            <Progress percent={completionPercent} strokeColor="white"
              trailColor="rgba(255,255,255,0.2)" showInfo={false} size="small" />
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Card hoverable style={{
          background: `linear-gradient(135deg, ${token.colorError} 0%, ${token.colorErrorActive} 100%)`,
          borderRadius: '12px', border: 'none',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: '100%'
        }} bodyStyle={{ padding: '20px' }}>
          <Statistic
            title={<Space style={{ color: 'white', opacity: 0.9 }}><WarningOutlined /><Text style={{ color: 'white' }}>Fora do Prazo</Text></Space>}
            value={foraPrazoGeral}
            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
            prefix={<WarningOutlined />}
          />
          <div style={{ marginTop: 12 }}>
            <Progress percent={overduePercent} strokeColor="white"
              trailColor="rgba(255,255,255,0.2)" showInfo={false} size="small" />
          </div>
        </Card>
      </Col>
    </Row>
  );

  // Tabela de projetos por direção
  const projetosTableColumns = [
    {
      title: 'Direção',
      dataIndex: 'direcao',
      key: 'direcao',
      ellipsis: true,
    },
    {
      title: 'Total',
      dataIndex: 'total_projetos',
      key: 'total',
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: 'Abertos',
      dataIndex: 'abertos',
      key: 'abertos',
      render: (val) => <Tag color="processing">{val}</Tag>,
    },
    {
      title: 'Concluídos',
      dataIndex: 'fechados',
      key: 'fechados',
      render: (val) => <Tag color="success">{val}</Tag>,
    },
    {
      title: 'Suspensos',
      dataIndex: 'suspensos',
      key: 'suspensos',
      render: (val) => <Tag color="warning">{val}</Tag>,
    },
    {
      title: 'Fora Prazo',
      dataIndex: 'fora_prazo',
      key: 'fora_prazo',
      render: (val) => <Tag color="error">{val}</Tag>,
    },
  ];

  // Colunas expandidas para funcionários na tabela de assiduidade
  const expandedFuncionariosColumns = [
    {
      title: 'Funcionário',
      dataIndex: 'nome',
      key: 'nome',
      render: (text) => <Space><UserOutlined />{text}</Space>,
    },
    {
      title: 'ID',
      dataIndex: 'employee_id',
      key: 'employee_id',
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'Assiduidade',
      dataIndex: 'assiduidade',
      key: 'assiduidade',
      render: (text) => (
        <Space>
          <Progress percent={parseInt(text) || 0} size="small" strokeColor={token.colorSuccess} showInfo={false} style={{ width: 80 }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Pontualidade',
      dataIndex: 'pontualidade',
      key: 'pontualidade',
      render: (text) => (
        <Space>
          <Progress percent={parseInt(text) || 0} size="small" strokeColor={token.colorPrimary} showInfo={false} style={{ width: 80 }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Trab./Úteis',
      key: 'dias',
      render: (_, record) => `${record.dias_trabalhados} / ${record.dias_uteis}`,
    },
    {
      title: 'Faltas (Just.)',
      key: 'faltas',
      render: (_, record) => (
        <Space>
          <Tag color="warning">{record.faltas}</Tag>
          {record.faltas_justificadas > 0 && <Tag>{record.faltas_justificadas} just.</Tag>}
        </Space>
      ),
    },
  ];

  const assiduidadeTableColumns = [
    {
      title: 'Direção',
      dataIndex: 'direcao',
      key: 'direcao',
      ellipsis: true,
    },
    {
      title: 'Total Func.',
      dataIndex: ['resumo', 'total_funcionarios'],
      key: 'total_func',
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: 'Média Assiduidade',
      dataIndex: ['resumo', 'media_assiduidade'],
      key: 'assid',
      render: (text) => (
        <Space>
          <Progress percent={parseInt(text) || 0} size="small" strokeColor={token.colorSuccess} showInfo={false} style={{ width: 80 }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Média Pontualidade',
      dataIndex: ['resumo', 'media_pontualidade'],
      key: 'pont',
      render: (text) => (
        <Space>
          <Progress percent={parseInt(text) || 0} size="small" strokeColor={token.colorPrimary} showInfo={false} style={{ width: 80 }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
  ];

  // Gráfico de tarefas mensais agregado
  const TarefasMensaisChart = () => {
  const dadosExibicao = useMemo(() => {
    if (direcaoSelecionada === 'todas') {
      return tarefasMensaisAgregado;
    }
    const direcao = tarefasMensaisDirecoes.find(d => d.id_direcao === Number(direcaoSelecionada));
    return direcao ? direcao.meses : [];
  }, [direcaoSelecionada, tarefasMensaisDirecoes, tarefasMensaisAgregado]);

  const opcoesSelect = useMemo(() => {
    const base = [{ label: 'Todas as Direções', value: 'todas' }];
    const direcoes = tarefasMensaisDirecoes.map(d => ({
      label: d.direcao,
      value: String(d.id_direcao),
    }));
    return [...base, ...direcoes];
  }, [tarefasMensaisDirecoes]);

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          <span>Tarefas Concluídas por Mês</span>
        </Space>
      }
      extra={
        <Select
          value={direcaoSelecionada}
          onChange={setDirecaoSelecionada}
          options={opcoesSelect}
          style={{ width: 280 }}
          size="small"
          prefix={<TeamOutlined />}
          // Estilização para combinar com o visual premium
          className="dashboard-select-direcao"
        />
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
      hoverable
    >
      <div style={{ height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={dadosExibicao}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorTarefasAdmin" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.8} />
                <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorder} />
            <XAxis dataKey="mes" tick={{ fill: token.colorTextSecondary }} axisLine={{ stroke: token.colorBorder }} />
            <YAxis tick={{ fill: token.colorTextSecondary }} axisLine={{ stroke: token.colorBorder }} allowDecimals={false} />
            <RechartsTooltip contentStyle={{
              backgroundColor: token.colorBgContainer,
              border: `1px solid ${token.colorBorder}`,
              borderRadius: '8px'
            }} />
            <Area
              type="monotone"
              dataKey="total"
              stroke={token.colorPrimary}
              fillOpacity={1}
              fill="url(#colorTarefasAdmin)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Content style={{ padding: '24px', overflow: 'auto', height: '100vh' }}>
        {loading && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.7)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1000
          }}>
            <Spin size="large" tip="Carregando métricas da administração..." />
          </div>
        )}

        <div style={{
          marginBottom: 24, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 16
        }}>
   
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Button icon={<SyncOutlined />} onClick={fetchData} loading={loading}>
              Atualizar
            </Button>
          </Space>
        </div>

        <ProjetosCards />

        <Card title="Projetos por Direção" style={{ borderRadius: '12px', marginBottom: '24px' }} hoverable>
          <Table
            columns={projetosTableColumns}
            dataSource={projetosDirecoes.map((d, i) => ({ ...d, key: i }))}
            pagination={false}
            size="small"
            loading={loading}
          />
        </Card>

        <TarefasMensaisChart />

        <Card title="Assiduidade por Direção" style={{ borderRadius: '12px', marginBottom: '24px' }} hoverable>
          <Table
            columns={assiduidadeTableColumns}
            expandable={{
              expandedRowRender: (record) => (
                record.funcionarios && record.funcionarios.length > 0 ? (
                  <Table
                    columns={expandedFuncionariosColumns}
                    dataSource={record.funcionarios.map((f, idx) => ({ ...f, key: idx }))}
                    pagination={false}
                    size="small"
                    rowKey="employee_id"
                  />
                ) : (
                  <Text type="secondary">Nenhum funcionário registrado.</Text>
                )
              ),
              rowExpandable: (record) => record.funcionarios && record.funcionarios.length > 0,
            }}
            dataSource={assiduidadeDirecoes.map((d, i) => ({ ...d, key: i }))}
            pagination={false}
            size="small"
            loading={loading}
          />
        </Card>

        <FloatButton.BackTop icon={<UpOutlined />} style={{ right: 24, bottom: 24 }} visibilityHeight={300} />
        <FloatButton icon={<SyncOutlined spin={loading} />} onClick={fetchData} tooltip="Atualizar dados" style={{ right: 24, bottom: 80 }} />
      </Content>
    </Layout>
  );
};

export default DashboardAdministracao;